import React, { useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Activity,
  CheckCircle2,
  Clock,
  Eye,
  ChevronRight,
  FileDown,
  Filter,
  Mail,
  Minus,
  MoreVertical,
  Send,
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { Button } from "../../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart as RechartsPieChart,
  ReferenceArea,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { Tooltip, TooltipProvider } from "../../../components/ui/tooltip";
import { Checkbox } from "../../../components/ui/checkbox";
import { Label } from "../../../components/ui/label";
import { useMediaQuery } from "@mui/material";
import moment from "moment";

const stringToColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str?.length; i++) {
    hash = str?.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 50%)`;
};

const SitesTabContent = ({ clients, setActiveTab, rawAppointments, dateRange }) => {

  const isMobile = useMediaQuery("(max-width:767px)");
  const [siteChartDropdownOpen, setSiteChartDropdownOpen] = React.useState(false);
  const [selectedSitesForChart, setSelectedSitesForChart] = React.useState([]);

  const filteredAppointments = useMemo(() => {
    if (!rawAppointments || !rawAppointments.length) return [];
    
    const now = moment();
    let rangeStartDate, rangeEndDate;

    switch (dateRange || "today") {
      case "today":
        rangeStartDate = now.format("YYYY-MM-DD");
        rangeEndDate = now.format("YYYY-MM-DD");
        break;
      case "yesterday":
        const yesterday = moment().subtract(1, "day");
        rangeStartDate = yesterday.format("YYYY-MM-DD");
        rangeEndDate = yesterday.format("YYYY-MM-DD");
        break;
      case "last7":
        rangeStartDate = moment().subtract(7, "days").format("YYYY-MM-DD");
        rangeEndDate = now.format("YYYY-MM-DD");
        break;
      case "last30":
      default:
        rangeStartDate = moment().subtract(30, "days").format("YYYY-MM-DD");
        rangeEndDate = now.format("YYYY-MM-DD");
        break;
    }

    return rawAppointments.filter(appt => {
      const apptDate = moment(appt.appointmentDateTime).format("YYYY-MM-DD");
      return apptDate >= rangeStartDate && apptDate <= rangeEndDate;
    });
  }, [rawAppointments, dateRange]);

  const getAppointmentStatus = (appt) => {
    const details = appt.apoointmentDetails || {};
    let shiftEnd = null;

    if (details.endTime && appt.appointmentDateTime) {
      const baseDate = moment(appt.appointmentDateTime);
      const timeStr = details.endTime;
      const dateStr = baseDate.format("YYYY-MM-DD");
      shiftEnd = moment(`${dateStr} ${timeStr}`, "YYYY-MM-DD hh:mm a");

      if (shiftEnd.isValid() && shiftEnd.isBefore(baseDate)) {
        shiftEnd.add(1, 'day');
      }
    } else if (appt.appointmentDateTime) {
      shiftEnd = moment(appt.appointmentDateTime);
    }

    const tasks = appt.tasks || {};
    let status = {
      isMissing: false,
      isLate: false,
      isInvalid: false,
      hasRequiredTasks: false,
      isCompliant: false
    };

    Object.values(tasks).forEach((group) => {
      group.tasksList?.forEach((task) => {
        if (task?.pos) {
          status.hasRequiredTasks = true;
          const proofs = task?.proofs || [];

          if (!proofs || proofs.length === 0) {
            status.isMissing = true;
          } else {
            const validProofs = proofs?.filter(p => p.verified === true && p.source === 'camera');

            if (validProofs.length === 0) {
              status.isInvalid = true;
            } else if (shiftEnd) {
              const hasLateProof = proofs.some(p => {
                let capturedAt = p.captured_at_device;
                if (capturedAt && typeof capturedAt === 'object' && capturedAt.seconds) {
                  capturedAt = moment.unix(capturedAt.seconds);
                } else {
                  capturedAt = moment(capturedAt);
                }
                return capturedAt.isValid() && capturedAt.isAfter(shiftEnd);
              });

              if (hasLateProof) status.isLate = true;
            }
          }
        }
      });
    });

    if (status.hasRequiredTasks) {
      status.isCompliant = !status.isMissing && !status.isInvalid && !status.isLate;
    }

    return status;
  };

  const siteMetrics = useMemo(() => {
    if (!filteredAppointments || !filteredAppointments.length) {
      return {
        highRisk: 0,
        attention: 0,
        compliant: 0,
        total: 0,
        distribution: { excellent: 0, good: 0, fair: 0, poor: 0 }
      };
    }

    const siteStats = {};

    filteredAppointments.forEach((appt) => {
      const status = getAppointmentStatus(appt);

      if (!status.hasRequiredTasks) return;

      const clientId = appt.clientId;
      if (!siteStats[clientId]) {
        siteStats[clientId] = {
          missing: 0,
          late: 0,
          invalid: 0,
          compliant: 0,
          total: 0,
        };
      }

      const stats = siteStats[clientId];
      stats.total++;

      if (status.isMissing) stats.missing++;
      else if (status.isInvalid) stats.invalid++;
      else if (status.isLate) stats.late++;
      else if (status.isCompliant) stats.compliant++;
    });

    let highRisk = 0;
    let attention = 0;
    let compliant = 0;
    let excellentDist = 0;
    let goodDist = 0;
    let fairDist = 0;
    let poorDist = 0;
    const totalSites = Object.keys(siteStats)?.length;

    Object.values(siteStats).forEach((site) => {
      if (site.total === 0) return;
      // Compliance Rate = (Strictly On-Time & Verified Shifts / Total shifts) * 100
      const complianceRate = site.total > 0 ? (site.compliant / site.total) * 100 : 0;

      // 1. High Risk Sites
      // Formula: 3+ missed proofs OR compliance < 70%
      if (site.missing >= 3 || (site.total > 0 && complianceRate < 70)) {
        highRisk++;
      }

      // 2. Sites Requiring Attention
      // Formula: missing > 0 OR late > 0
      if (site.missing > 0 || site.late > 0) {
        attention++;
      }

      // 3. Compliant Sites
      // Formula: missing = 0 AND late = 0 AND invalid = 0
      if (site.missing === 0 && site.late === 0 && site.invalid === 0) {
        compliant++;
      }

      // Distribution Categories
      if (complianceRate >= 90) {
        excellentDist++;
      } else if (complianceRate >= 75) {
        goodDist++;
      } else if (complianceRate >= 60) {
        fairDist++;
      } else {
        poorDist++;
      }
    });

    return { highRisk, attention, compliant, total: totalSites, distribution: { excellent: excellentDist, good: goodDist, fair: fairDist, poor: poorDist } };
  }, [filteredAppointments]);

  const attentionSites = useMemo(() => {
    if (!filteredAppointments || !filteredAppointments.length) return [];

    const sitesMap = {};
    const clientList = Array.isArray(clients) ? clients : Object.values(clients || {});

    filteredAppointments.forEach((appt) => {
      const clientId = appt.clientId;
      if (!sitesMap[clientId]) {
        const client = clientList.find(c => c.id === clientId);
        sitesMap[clientId] = {
          id: clientId,
          name: client?.clientName || client?.firstName || 'Unknown Site',
          city: client?.addressInfo?.city || '-',
          state: client?.addressInfo?.state || '-',
          totalShifts: 0,
          compliantShifts: 0,
          missingProofs: 0,
          lateProofs: 0,
          invalidProofs: 0,
          employees: new Set(),
          employeesWithIssues: new Set(),
          lastIssueTime: 0,
        };
      }

      const status = getAppointmentStatus(appt);

      if (status.hasRequiredTasks) {
        const site = sitesMap[clientId];
        site.totalShifts++;
        if (appt.userId) site.employees.add(appt.userId);

        let hasIssue = false;
        if (status.isMissing) {
          site.missingProofs++;
          hasIssue = true;
        } else if (status.isInvalid) {
          site.invalidProofs++;
          hasIssue = true;
        } else if (status.isLate) {
          site.lateProofs++;
          hasIssue = true;
        } else if (status.isCompliant) {
          site.compliantShifts++;
        }

        if (hasIssue) {
          if (appt.userId) site.employeesWithIssues.add(appt.userId);
          const issueTime = moment(appt.appointmentDateTime).valueOf();
          if (issueTime > site.lastIssueTime) {
            site.lastIssueTime = issueTime;
          }
        }
      }
    });

    return Object.values(sitesMap)
      .map(site => ({
        ...site,
        complianceRate: site.totalShifts > 0 ? Math.round((site.compliantShifts / site.totalShifts) * 100) : 0,
        atRiskLabel: `${site.employeesWithIssues.size}/${site.employees.size}`
      }))
      .filter(site => site.missingProofs > 0 || site.lateProofs > 0 || site.invalidProofs > 0)
      .sort((a, b) => b.lastIssueTime - a.lastIssueTime);

  }, [filteredAppointments, clients]);

  const complianceTrends = useMemo(() => {
    if (!filteredAppointments || !filteredAppointments.length) return [];

    const dataByDate = {};
    const clientMap = {};
    const list = Array.isArray(clients) ? clients : Object.values(clients || {});
    list.forEach(c => {
      const id = c.id || c._id;
      if (id) clientMap[id] = c.clientName || c.firstName || 'Unknown';
    });

    filteredAppointments.forEach(appt => {
      const status = getAppointmentStatus(appt);
      
      // ✅ Only process appointments with POS tasks
      if (!status.hasRequiredTasks) return;

      const dateStr = moment(appt.appointmentDateTime).format("MMM D");
      const clientId = appt.clientId;
      const clientName = clientMap[clientId] || 'Unknown';

      if (!dataByDate[dateStr]) {
        dataByDate[dateStr] = {};
      }
      if (!dataByDate[dateStr][clientName]) {
        dataByDate[dateStr][clientName] = { total: 0, compliant: 0 };
      }

      const stats = dataByDate[dateStr][clientName];
      stats.total++;

      if (status.isCompliant) {
        stats.compliant++;
      }
    });

    const sortedDates = Object.keys(dataByDate).sort((a, b) => 
      moment(a, "MMM D").valueOf() - moment(b, "MMM D").valueOf()
    );

    return sortedDates.map(date => {
      const entry = { date };
      Object.keys(dataByDate[date]).forEach(clientName => {
        const { total, compliant } = dataByDate[date][clientName];
        entry[clientName] = total > 0 ? Math.round((compliant / total) * 100) : 0;
      });
      return entry;
    });
  }, [filteredAppointments, clients]);

  const availableSites = useMemo(() => {
    if (!complianceTrends || complianceTrends.length === 0) return [];
    
    const sitesSet = new Set();
    complianceTrends.forEach(dayData => {
      Object.keys(dayData).forEach(key => {
        if (key !== 'date') sitesSet.add(key);
      });
    });
    
    return Array.from(sitesSet);
  }, [complianceTrends]);

  const siteComplianceAverages = useMemo(() => {
    if (!complianceTrends || complianceTrends.length === 0) return [];

    const siteStats = {};

    complianceTrends?.forEach(dayData => {
      Object.keys(dayData).forEach(siteName => {
        if (siteName === 'date') return;
        
        if (!siteStats[siteName]) {
          siteStats[siteName] = { total: 0, sum: 0 };
        }
        
        const complianceRate = dayData[siteName];
        if (typeof complianceRate === 'number') {
          siteStats[siteName].sum += complianceRate;
          siteStats[siteName].total++;
        }
      });
    });

    return Object.entries(siteStats)
      ?.map(([siteName, stats]) => ({
        siteName,
        avgCompliance: stats.total > 0 ? stats.sum / stats.total : 0
      }))
      ?.sort((a, b) => a.avgCompliance - b.avgCompliance);
  }, [complianceTrends]);

  React.useEffect(() => {
    if (siteComplianceAverages.length > 0 && selectedSitesForChart.length === 0) {
      const worstSites = siteComplianceAverages?.slice(0, 5)?.map(s => s.siteName);
      setSelectedSitesForChart(worstSites);
    }
  }, [siteComplianceAverages]);

  const clientLineConfig = useMemo(() => {
    const config = {};
    
    availableSites.forEach(siteName => {
      const siteAvg = siteComplianceAverages?.find(s => s.siteName === siteName);
      const avgCompliance = siteAvg?.avgCompliance || 0;
      
      const isWorstPerformer = siteComplianceAverages?.slice(0, 5)?.some(s => s.siteName === siteName);
      
      config[siteName] = {
        color: stringToColor(siteName),
        strokeWidth: isWorstPerformer ? 3 : 2,
        label: siteName,
        avgCompliance: Math.round(avgCompliance)
      };
    });
    
    return config;
  }, [availableSites, siteComplianceAverages]);

  const toggleSiteForChart = (siteName) => {
    setSelectedSitesForChart(prev =>
      prev.includes(siteName)
        ? prev.filter(s => s !== siteName)
        : [...prev, siteName]
    );
  };

  const toggleAllSites = () => {
    if (selectedSitesForChart.length === availableSites.length) {
      const worstSites = siteComplianceAverages?.slice(0, 5)?.map(s => s.siteName);
      setSelectedSitesForChart(worstSites);
    } else {
      setSelectedSitesForChart(availableSites?.slice(0, 10));
    }
  };


  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500 shadow-md hover:shadow-lg transition-all dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
          <CardContent className="p-24">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-4">
                  Total Sites Monitored
                </p>
                <p className="text-3xl font-bold text-blue-600">
                  {siteMetrics.total}
                </p>
                <p className="text-xs text-muted-foreground mt-4">
                  Active locations
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Activity className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500 shadow-md hover:shadow-lg transition-all dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
          <CardContent className="p-24">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-4">Compliant Sites</p>
                <p className="text-3xl font-bold text-green-600">
                  {siteMetrics.compliant}
                </p>
                <p className="text-xs text-muted-foreground mt-4">
                  {siteMetrics.total > 0 ? Math.round((siteMetrics.compliant / siteMetrics.total) * 100) : 0}% of total
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500 shadow-md hover:shadow-lg transition-all dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
          <CardContent className="p-24">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-4">
                  Sites Requiring Attention
                </p>
                <p className="text-3xl font-bold text-orange-600">
                  {siteMetrics.attention}
                </p>
                <p className="text-xs text-muted-foreground mt-4">
                  Any missing/late proofs
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500 shadow-md hover:shadow-lg transition-all dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
          <CardContent className="p-24">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-4">
                  High Risk Sites
                </p>
                <p className="text-3xl font-bold text-red-600">
                  {siteMetrics.highRisk}
                </p>
                <p className="text-xs text-muted-foreground mt-4">
                  3+ missed OR &lt; 70% comp.
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
        <CardHeader className="p-16">
          <CardTitle className="text-xl font-bold">
            Sites Requiring Attention
          </CardTitle>
          <CardDescription>
            Detailed list of sites that need operator intervention
          </CardDescription>
        </CardHeader>
        <CardContent className="p-16">
          {isMobile ? (
            <div className="space-y-3">
              {attentionSites?.map((site, idx) => (
                <Card
                  key={site.id}
                  className="cursor-pointer hover:shadow-md transition-all animate-in fade-in border-l-4"
                  style={{
                    animationDelay: `${idx * 50}ms`,
                    borderLeftColor:
                      site.compliance < 70
                        ? "#ef4444"
                        : site.compliance < 85
                          ? "#f59e0b"
                          : "#10b981",
                  }}
                  onClick={() =>
                    console.log("Open site detail sheet:", site?.name)
                  }
                >
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-base truncate">
                            {site?.name}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {site?.city}, {site?.state}
                          </p>
                        </div>
                        <Badge
                          variant={
                            site?.complianceRate < 70
                              ? "destructive"
                              : site?.complianceRate < 85
                                ? "secondary"
                                : "default"
                          }
                          className="shrink-0 text-sm font-semibold px-3 py-1"
                        >
                          {site?.complianceRate}%
                        </Badge>
                      </div>

                      <div className="space-y-2 pt-4 border-t text-sm">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">
                              Missing:
                            </span>
                            <span className="font-semibold text-red-600">
                              {site?.missingProofs}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Late:</span>
                            <span className="font-semibold text-orange-600">
                              {site?.lateProofs}
                            </span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">
                              At Risk:
                            </span>
                            <span className="font-semibold">
                              {site?.atRiskLabel}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">
                              Last Issue:
                            </span>
                            <span className="font-semibold text-xs">
                              {site?.lastIssueTime
                                ? moment(site?.lastIssueTime).format(
                                  "MMM DD, HH:mm",
                                )
                                : "-"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Site Name</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-center">Compliance Rate</TableHead>
                    <TableHead className="text-center">Missing Proofs</TableHead>
                    <TableHead className="text-center">Late Proofs</TableHead>
                    <TableHead className="text-center">Invalid Proofs</TableHead>
                    <TableHead className="text-center">At Risk</TableHead>
                    <TableHead className="text-right">Last Issue</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="bg-white dark:bg-[#1A1F2C]">
                  {attentionSites?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground bg-white dark:bg-[#1A1F2C]">
                        No sites requiring attention.
                      </TableCell>
                    </TableRow>
                  ) : (
                    attentionSites?.map((site) => (
                      <TableRow
                        key={site.id}
                        className="cursor-pointer transition-colors bg-white dark:bg-[#1A1F2C]"
                        onClick={() => { }}
                      >
                        <TableCell className="font-medium">{site?.name}</TableCell>
                        <TableCell>{site?.city}, {site?.state}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={site?.complianceRate < 70 ? "destructive" : "secondary"}>
                            {site?.complianceRate}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center text-red-600 font-medium">{site?.missingProofs}</TableCell>
                        <TableCell className="text-center text-orange-600 font-medium">{site?.lateProofs}</TableCell>
                        <TableCell className="text-center text-yellow-600 font-medium">{site?.invalidProofs}</TableCell>
                        <TableCell className="text-center">{site?.atRiskLabel}</TableCell>
                        <TableCell className="text-right text-muted-foreground text-xs">
                          {site?.lastIssueTime ? moment(site?.lastIssueTime).format("MMM DD, HH:mm") : "-"}
                        </TableCell>
                        <TableCell>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Site Compliance Distribution - ALWAYS VISIBLE */}
      <Card className="shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
        <CardHeader className="p-16">
          <CardTitle className="text-lg font-bold">
            Site Compliance Distribution
          </CardTitle>
          <CardDescription>
            Visual breakdown of all sites by compliance rate
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 p-16">
          {/* Doughnut Chart - NO internal labels */}
          <ResponsiveContainer width="100%" height={isMobile ? 200 : 260}>
            <RechartsPieChart>
              <Pie
                data={[
                  {
                    name: "Excellent",
                    value: siteMetrics.distribution.excellent,
                    color: "#10b981",
                  },
                  {
                    name: "Good",
                    value: siteMetrics.distribution.good,
                    color: "#3b82f6",
                  },
                  {
                    name: "Fair",
                    value: siteMetrics.distribution.fair,
                    color: "#f59e0b",
                  },
                  {
                    name: "Poor",
                    value: siteMetrics.distribution.poor,
                    color: "#ef4444",
                  },
                ]}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={false}
                outerRadius={isMobile ? 70 : 90}
                innerRadius={isMobile ? 40 : 55}
                fill="#8884d8"
                dataKey="value"
              >
                <Cell fill="#10b981" />
                <Cell fill="#3b82f6" />
                <Cell fill="#f59e0b" />
                <Cell fill="#ef4444" />
              </Pie>
              <TooltipProvider>
                <Tooltip />
              </TooltipProvider>
            </RechartsPieChart>
          </ResponsiveContainer>

          {/* External Legend Table - Mobile Readable */}
          <div className="space-y-2 px-2">
          {(() => {
            const total =
              siteMetrics.distribution.excellent +
              siteMetrics.distribution.good +
              siteMetrics.distribution.fair +
              siteMetrics.distribution.poor;

            if (total === 0) {
              return (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No sites with POS task requirements found</p>
                  <p className="text-xs mt-4">
                    Distribution will appear once appointments with proof-of-service tasks are available
                  </p>
                </div>
              );
            }

            // ✅ Show normal distribution when data exists
            return [
              {
                name: "Excellent",
                value: siteMetrics.distribution.excellent,
                color: "#10b981",
                desc: "90-100% compliance",
              },
              {
                name: "Good",
                value: siteMetrics.distribution.good,
                color: "#3b82f6",
                desc: "75-89% compliance",
              },
              {
                name: "Fair",
                value: siteMetrics.distribution.fair,
                color: "#f59e0b",
                desc: "60-74% compliance",
              },
              {
                name: "Poor",
                value: siteMetrics.distribution.poor,
                color: "#ef4444",
                desc: "< 60% compliance",
              },
            ].map((entry) => {
              const percentage = Math.round((entry.value / total) * 100);
              return (
                <div
                  key={entry.name}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className="h-3 w-3 rounded-full shrink-0"
                      style={{ backgroundColor: entry.color }}
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium block">
                        {entry.name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {entry.desc}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold">
                      {entry.value} {entry.value === 1 ? "site" : "sites"}
                    </span>
                    <span className="text-sm text-muted-foreground w-12 text-right">
                      {percentage}%
                    </span>
                  </div>
                </div>
              );
            });
          })()}
        </div>

          {/* Action Button */}
          <Button
            variant="outline"
            className="w-full mt-2"
            onClick={() => {
              setActiveTab("sites");
              // Optionally filter to critical sites
            }}
          >
            View Critical Sites →
          </Button>
        </CardContent>
      </Card>

      {/* Multi-line Site Compliance Trends - ALWAYS VISIBLE WITH FILTER */}
      <Card className="shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
        <CardHeader className="p-16">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg font-bold">
                Site Compliance Trends (7 Days)
              </CardTitle>
              <CardDescription>
                Compare trends across selected sites - worst performers highlighted
              </CardDescription>
            </div>
            <Popover
              open={siteChartDropdownOpen}
              onOpenChange={setSiteChartDropdownOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full sm:w-auto justify-start"
                >
                  <Filter className="h-4 w-4 mr-8" />
                  {selectedSitesForChart?.length === 0
                    ? "No Sites"
                    : selectedSitesForChart?.length === availableSites?.length
                    ? "All Sites"
                    : `${selectedSitesForChart?.length} Site${selectedSitesForChart?.length !== 1 ? "s" : ""}`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[320px]" align="end">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm">
                      Select Sites to Display
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleAllSites}
                      className="h-7 text-xs"
                    >
                      {selectedSitesForChart?.length === availableSites?.length
                        ? "Reset to Top 5"
                        : "Select All"}
                    </Button>
                  </div>
                  
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {siteComplianceAverages.map(({ siteName, avgCompliance }, index) => {
                      const isWorstPerformer = index < 5;
                      return (
                        <div
                          key={siteName}
                          className="flex items-center space-x-2 p-9 rounded-md hover:bg-muted cursor-pointer"
                          onClick={() => toggleSiteForChart(siteName)}
                        >
                          <Checkbox
                            id={`site-${siteName}`}
                            checked={selectedSitesForChart.includes(siteName)}
                            onCheckedChange={() => toggleSiteForChart(siteName)}
                          />
                          <Label
                            htmlFor={`site-${siteName}`}
                            className="font-normal cursor-pointer flex-1 flex items-center justify-between"
                          >
                            <span className="flex items-center gap-2">
                              {siteName}
                              {isWorstPerformer && (
                                <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200 px-2 py-0.5 rounded">
                                  Top 5 Worst
                                </span>
                              )}
                            </span>
                            <span className={`text-xs font-semibold ${
                              avgCompliance >= 90 ? 'text-green-600' :
                              avgCompliance >= 75 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {Math.round(avgCompliance)}%
                            </span>
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </CardHeader>
        <CardContent className="p-16">
          {complianceTrends?.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-sm">No compliance data available</p>
              <p className="text-xs mt-4">Data will appear once appointments with proof-of-service tasks are recorded</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={isMobile ? 240 : 350}>
              <LineChart
                data={complianceTrends}
                margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" tick={{ fontSize: isMobile ? 11 : 12 }} />
                <YAxis
                  tick={{ fontSize: isMobile ? 11 : 12 }}
                  domain={[0, 100]}
                  width={isMobile ? 35 : 45}
                />
                {/* <ReferenceArea y1={90} y2={100} fill="#10b981" fillOpacity={0.1} />
                <ReferenceArea y1={75} y2={90} fill="#f59e0b" fillOpacity={0.1} />
                <ReferenceArea y1={0} y2={75} fill="#ef4444" fillOpacity={0.1} />
                 */}
                <TooltipProvider>
                  <Tooltip />
                </TooltipProvider>
                <Legend wrapperStyle={{ fontSize: isMobile ? "12px" : "14px" }} />
                
                {selectedSitesForChart?.map((site) => {
                  const config = clientLineConfig?.[site];
                  if (!config) return null;

                  return (
                    <Line
                      key={site}
                      type="monotone"
                      dataKey={site}
                      stroke={config.color}
                      strokeWidth={config.strokeWidth}
                      name={`${config.label} (${config.avgCompliance}%)`}
                      connectNulls
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Advanced View: Additional Charts */}
      {/* {showAdvancedView && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <Card className="shadow-md">
            <CardHeader>
              <CardTitle className="text-lg font-bold">
                Proof Coverage by Site
              </CardTitle>
              <CardDescription>
                Breakdown of provided, late, and missing proofs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={isMobile ? 320 : 400}>
                <BarChart
                  data={mockSites.map((s) => ({
                    site: s.name,
                    provided: s.onTimeProofs,
                    late: s.lateProofs,
                    missing: s.missingProofs,
                  }))}
                  layout="vertical"
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: isMobile ? 11 : 12 }}
                  />
                  <YAxis
                    dataKey="site"
                    type="category"
                    tick={{ fontSize: isMobile ? 9 : 11 }}
                    width={isMobile ? 120 : 150}
                  />
                  <Tooltip />
                  <Legend
                    wrapperStyle={{ fontSize: isMobile ? "12px" : "14px" }}
                  />
                  <Bar
                    dataKey="provided"
                    stackId="a"
                    fill="#10b981"
                    name="Provided On Time"
                    radius={[0, 4, 4, 0]}
                  />
                  <Bar dataKey="late" stackId="a" fill="#f59e0b" name="Late" />
                  <Bar
                    dataKey="missing"
                    stackId="a"
                    fill="#ef4444"
                    name="Missing"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )} */}
    </>
  );
};

export default SitesTabContent;